import { useQuery } from "@tanstack/react-query";
import axios from "axios";

/**
 * A user's address book.
 *
 * `users.address_id` is the DEFAULT address — one entry of this list, flagged
 * `is_default` — not a separate record. Every endpoint returns the WHOLE list
 * back so a caller never has to reconcile local state after a mutation.
 *
 * Used in two places: the address book on the Users page, and the "Address"
 * picker under the Xero organisation in the PAYMENTS section.
 */
const base = (userId) => `/api/users/${userId}/addresses`;

export const useUserAddresses = (userId, enabled = true) =>
  useQuery({
    queryKey: ["user-addresses", userId],
    queryFn: async () => {
      const { data } = await axios.get(base(userId));
      return data.addresses || [];
    },
    enabled: !!userId && enabled,
    staleTime: 60 * 1000,
  });

export const createUserAddress = async (userId, payload) => {
  const { data } = await axios.post(base(userId), payload);
  return data.addresses || [];
};

export const updateUserAddress = async (userId, addressId, payload) => {
  const { data } = await axios.put(`${base(userId)}/${addressId}`, payload);
  return data.addresses || [];
};

/** Promote an address to the user's default (writes users.address_id). */
export const setDefaultUserAddress = async (userId, addressId) => {
  const { data } = await axios.put(`${base(userId)}/${addressId}/default`);
  return data.addresses || [];
};

/** Any address may be deleted, including the default and the last one. The
 *  server promotes another entry to default first where one exists. */
export const deleteUserAddress = async (userId, addressId) => {
  const { data } = await axios.delete(`${base(userId)}/${addressId}`);
  return data.addresses || [];
};

/** Pin which address an order's Xero invoice is billed to. */
export const setOrderXeroAddress = async (orderId, addressId) => {
  const { data } = await axios.post(`/api/order-management/${orderId}/xero-address`, {
    xero_address_id: addressId,
  });
  return data;
};
